import SEOHead from './SEOHead';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Calendar, MapPin, Search, Users, Sparkles } from 'lucide-react';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const Events = () => {
  const { user } = useContext(AuthContext); // eslint-disable-line no-unused-vars
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eventType, setEventType] = useState('all');

  useEffect(() => { fetchEvents(); }, []); // eslint-disable-line

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (eventType !== 'all') params.event_type = eventType;
      const res = await axios.get(`${API}/api/events`, { params });
      setEvents(res.data.events || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return 'TBA';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const calcTotal = (fee) => {
    const gst = Math.round(fee * 0.18);
    return fee + gst;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead
        title="Events & Workshops"
        description="Discover and register for student events, workshops, webinars and conferences on Nerd Nation."
        path="/events"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 text-orange-500" />
            Events
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Workshops, webinars, conferences and bootcamps curated for students.</p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); fetchEvents(); }} className="flex flex-col sm:flex-row gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="events-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events…"
              className="pl-10"
            />
          </div>
          <select
            data-testid="events-type-filter"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="px-3 py-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            <option value="all">All types</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>
          <button type="submit" className="px-5 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium">Search</button>
        </form>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : events.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <Sparkles className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">No events yet</p>
            <p className="text-sm text-gray-500">Check back soon — exciting events coming up!</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {events.map((ev) => (
              <Card
                key={ev.id}
                onClick={() => navigate(`/events/${ev.slug || ev.id}`)}
                data-testid={`event-card-${ev.id}`}
                className="group cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden border-gray-200"
              >
                <div className="relative aspect-video bg-gradient-to-br from-orange-100 to-amber-50 overflow-hidden">
                  {ev.banner_url ? (
                    <img src={ev.banner_url} alt={ev.title} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Sparkles className="h-12 w-12 text-orange-300" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 right-3 ${ev.event_type === 'free' ? 'bg-green-500' : 'bg-blue-600'} text-white border-0`}>
                    {ev.event_type === 'free' ? 'FREE' : `₹${calcTotal(ev.fee).toLocaleString('en-IN')}`}
                  </Badge>
                </div>
                <CardContent className="p-4 sm:p-5">
                  {ev.category && <Badge variant="outline" className="text-[10px] mb-2">{ev.category}</Badge>}
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-snug line-clamp-2">{ev.title}</h3>
                  <div className="flex flex-col gap-1.5 mt-3 text-xs sm:text-sm text-gray-600">
                    {ev.event_date && (
                      <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{formatDate(ev.event_date)}</div>
                    )}
                    {(ev.venue || ev.online_link) && (
                      <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{ev.venue || 'Online'}</div>
                    )}
                    {typeof ev.registrations_count === 'number' && (
                      <div className="flex items-center gap-1.5 text-orange-600 font-medium"><Users className="h-3.5 w-3.5" />{ev.registrations_count} registered</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Events;
